export type UniformDefinition = {
  name: string;
  dataType: string;
  isArray: boolean;
  arraySize?: number;
};

export type AttributeDefinition = {
  name: string;
  dataType: string;
  location?: number;
};

export type ShaderParseResult = {
  uniforms: UniformDefinition[];
  attributes: AttributeDefinition[];
  defines: string[];
};

export class ShaderSourceParser {
  public parse(source: string): ShaderParseResult {
    let attributes: AttributeDefinition[] = [];
    let uniforms: UniformDefinition[] = [];
    let defines: Set<string> = new Set<string>();

    let attrRegex =
      /^(?:layout\s*\(\s*location\s*=\s*(\d+)\s*\)\s*)?in\s+([a-z0-9]+)\s+([a-z0-9]+)\s*;/gim;
    let matchAttr: RegExpExecArray | null;
    while ((matchAttr = attrRegex.exec(source))) {
      attributes.push({
        name: matchAttr[3],
        dataType: matchAttr[2],
        location: matchAttr[1] !== undefined ? parseInt(matchAttr[1]) : undefined,
      });
    }

    let uniformRegex = /^uniform\s+([a-z0-9\[\]]+)\s+([a-z0-9\[\]]+)\s*;/gim;
    let matchUniform: RegExpExecArray | null;
    while ((matchUniform = uniformRegex.exec(source))) {
      let arrayMatch = matchUniform[2].match(/(.*?)\[(\d+)]/);
      if (arrayMatch) {
        let name = arrayMatch[1];
        let size = parseInt(arrayMatch[2]);
        uniforms.push({
          dataType: matchUniform[1],
          name: name,
          isArray: true,
          arraySize: size,
        });
      } else {
        uniforms.push({
          dataType: matchUniform[1],
          name: matchUniform[2],
          isArray: false,
        });
      }
    }

    let defineRegex = /^\s*#[a-z]+\s+NOODLE_([a-z0-9_]+)$/gim;
    let matchDefine: string[] | null;
    while ((matchDefine = defineRegex.exec(source))) {
      defines.add(matchDefine[1]);
    }

    return {
      uniforms: uniforms,
      attributes: attributes,
      defines: [...defines],
    };
  }

  public merge(...results: ShaderParseResult[]): ShaderParseResult {
    let attributes = new Map<string, AttributeDefinition>();
    let uniforms = new Map<string, UniformDefinition>();
    let defines = new Set<string>();

    for (let result of results) {
      result.attributes.forEach((attr) => attributes.set(attr.name, attr));
      result.uniforms.forEach((uniform) => uniforms.set(uniform.name, uniform));
      result.defines.forEach((define) => defines.add(define));
    }

    return {
      attributes: [...attributes.values()],
      uniforms: [...uniforms.values()],
      defines: [...defines],
    };
  }

  public preprocess(source: string, settings: string[]): string {
    let parseResult = this.parse(source);
    let map = new Map<string, boolean>();
    parseResult.defines.forEach((def) => map.set(def, false));
    settings.forEach((def) => map.set(def, true));

    let lines: string[] = [];
    map.forEach((enabled, setting) => {
      lines.push(`var ${setting} = ${enabled};`);
    });
    lines.push(`var result = '';`);

    let preprocessRegex = /#([a-z]+)\s*(?:NOODLE_([a-z0-9_]+))?/i;
    for (let line of source.split('\n')) {
      let match = preprocessRegex.exec(line);
      if (match) {
        switch (match[1]) {
          case 'ifdef':
            lines.push(`if (${match[2]}) {`);
            break;
          case 'ifndef':
            lines.push(`if (!${match[2]}) {`);
            break;
          case 'else':
            lines.push(`} else {`);
            break;
          case 'endif':
            lines.push(`}`);
            break;
        }
      } else {
        lines.push(`result += \`${line}\\n\`;`);
      }
    }

    let result = eval(lines.join('\n')) as string;
    while (result.indexOf('\n\n') != -1) {
      result = result.replace('\n\n', '\n');
    }
    return result;
  }
}
